import { useEffect, useState, useMemo } from "react";
import { collection, onSnapshot, query, where, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import { THEME_COLOR } from "../config/club";

export default function JoinerMatchHistory({ clubId, uid, sessionId, setLastUpdate }) {
    const [history, setHistory] = useState([]);
    const THEME_VIOLET = THEME_COLOR;

    useEffect(() => {
        if (!clubId || !uid) return;

        const q = query(
            collection(db, "clubs", clubId, "sessions", sessionId, "matches"),
            where("status", "==", "finished"),
            orderBy("endedAt", "desc")
        );

        return onSnapshot(q, snap => {
            const list = snap.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .filter(m => m.players?.some(p => p.id === uid));

            setHistory(list);
            setLastUpdate(Date.now());
        });
    }, [clubId, uid, sessionId]);

    const stats = useMemo(() => {
        let games = 0, wins = 0, losses = 0, draws = 0;

        history.forEach(m => {
            if (!m.score || typeof m.score !== "string") return;
            const parts = m.score.split("-").map(n => parseInt(n, 10));
            if (parts.length !== 2 || isNaN(parts[0])) return;

            const [scoreA, scoreB] = parts;
            const isTeamA = m.players.slice(0, 2).some(p => p.id === uid);
            const isTeamB = m.players.slice(2, 4).some(p => p.id === uid);

            if (!isTeamA && !isTeamB) return;
            games++;

            if (scoreA === scoreB) {
                draws++;
            } else {
                const winner = scoreA > scoreB ? "A" : "B";
                if ((winner === "A" && isTeamA) || (winner === "B" && isTeamB)) wins++;
                else losses++;
            }
        });

        return { 
            games, 
            wins, 
            losses, 
            draws, 
            winRate: games > 0 ? Math.round((wins / games) * 100) : 0 
        };
    }, [history, uid]);

    if (history.length === 0)
        return (
            <div 
                className="mt-4 text-center py-5"
                style={{ color: "var(--muted-text)", opacity: 0.6 }}
            >
                No match history.
            </div>
        );

    return (
        <div className="mt-4 px-2 pb-5">
            <h6 
                className="fw-bold mb-3 text-uppercase small"
                style={{ color: THEME_VIOLET, letterSpacing: "1px" }}
            >
                Performance
            </h6>

            {/* STATS CARD */}
            <div 
                className="card border-0 shadow-lg rounded-4 overflow-hidden mb-4"
                style={{ 
                    background: `linear-gradient(135deg, ${THEME_VIOLET} 0%, #2e084d 100%)` 
                }}
            >
                <div className="card-body text-white p-4">
                    <div className="d-flex justify-content-between align-items-center mb-3 text-uppercase small opacity-75 fw-bold">
                        <span>Stats</span>
                        <span style={{ color: '#d8b4fe' }}>
                            {stats.winRate}% Win Rate
                        </span>
                    </div>

                    <div className="row g-0 text-center">
                        <div className="col-3 border-end" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
                            <div className="fs-4 fw-bold text-white">{stats.wins}</div>
                            <div className="small opacity-50">Wins</div>
                        </div>
                        <div className="col-3 border-end" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
                            <div className="fs-4 fw-bold text-white">{stats.losses}</div>
                            <div className="small opacity-50">Loss</div>
                        </div>
                        <div className="col-3 border-end" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
                            <div className="fs-4 fw-bold text-white">{stats.draws}</div>
                            <div className="small opacity-50">Draw</div>
                        </div>
                        <div className="col-3">
                            <div className="fs-4 fw-bold text-white">{stats.games}</div>
                            <div className="small opacity-50">Total</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* MATCH LIST */}
            <div className="list-unstyled">
                {history.map((m) => {
                    const teamA = m.players.slice(0, 2);
                    const teamB = m.players.slice(2, 4);
                    const scores = m.score.split("-").map(n => parseInt(n, 10));
                    const isDraw = scores[0] === scores[1];
                    const teamAWins = scores[0] > scores[1];
                    const teamBWins = scores[1] > scores[0];
                    const isUserInA = teamA.some(p => p.id === uid);
                    const userWon = (isUserInA && teamAWins) || (!isUserInA && teamBWins);

                    let borderCol = "#6c757d";
                    if (isDraw) borderCol = "#a855f7";
                    else if (userWon) borderCol = "#ffc107";
                    else borderCol = "#ef4444";

                    const ended = m.endedAt?.toMillis
                        ? new Date(m.endedAt.toMillis())
                        : new Date(m.endedAt);

                    return (
                        <div
                            key={m.id}
                            className="card border-0 shadow-sm rounded-4 p-3 mb-3"
                            style={{
                                borderLeft: `6px solid ${borderCol}`,
                                background: "var(--card-bg)"
                            }}
                        >
                            <div className="d-flex justify-content-between mb-2 align-items-center">
                                <span 
                                    className="fw-bold small"
                                    style={{ color: "var(--muted-text)" }}
                                >
                                    COURT {m.court}
                                </span>

                                <span
                                    className="fw-bold small"
                                    style={{
                                        color: isDraw
                                            ? "#a855f7"
                                            : userWon
                                            ? "#10b981"
                                            : "#ef4444"
                                    }}
                                >
                                    {isDraw ? "● DRAW" : userWon ? "★ VICTORY" : "DEFEAT"}
                                </span>
                            </div>

                            <div className="vstack gap-2">
                                <div
                                    className="d-flex justify-content-between align-items-center p-2 rounded-3"
                                    style={{
                                        background: teamAWins
                                            ? THEME_VIOLET
                                            : "var(--surface-bg)",
                                        color: teamAWins
                                            ? "#fff"
                                            : "var(--text-color)"
                                    }}
                                >
                                    <span className="fw-bold px-2">
                                        {teamA.map(p => p.name).join(" & ")}
                                    </span>
                                    <span className="fs-5 fw-black px-2">
                                        {scores[0]}
                                    </span>
                                </div>

                                <div
                                    className="d-flex justify-content-between align-items-center p-2 rounded-3"
                                    style={{
                                        background: teamBWins
                                            ? THEME_VIOLET
                                            : "var(--surface-bg)",
                                        color: teamBWins
                                            ? "#fff"
                                            : "var(--text-color)"
                                    }}
                                >
                                    <span className="fw-bold px-2">
                                        {teamB.map(p => p.name).join(" & ")}
                                    </span>
                                    <span className="fs-5 fw-black px-2">
                                        {scores[1]}
                                    </span>
                                </div>
                            </div>

                            <div
                                className="mt-2 text-center small"
                                style={{ color: "var(--muted-text)", opacity: 0.6 }}
                            >
                                {ended.toLocaleDateString([], { month: "short", day: "numeric" })} •{" "}
                                {ended.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}